// @ts-nocheck

import { getAdLinks } from 'client/js/redux/domains/AdLinks/actions';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import Button from '../../common/components/core/Button';
import { fetchRules } from '../actions/JourneyActions';
import { ImageService } from '../services/ImageService/ImageService';
import { LOADING_STATUS } from '../support/constants';

/**
 * Configure message and cta link shown for when product hasn't been used yet.
 * This is different than the message shown when no data is present for the filters selected.
 * To add a new product:
 * 1. add to PRODUCTS object with path and message
 * 2. fetch data (in componentWillMount) that you want to check for product usage (i.e. ad links or journeys created)
 * 3. add to checkForUsage and isLoading
 */
const PRODUCTS = {
  ADS: {
    path: '/ads/links/ad-links?modal=ad-link',
    message: "You don't have any ads data. Enable an ad partner and create a link.",
  },
  JOURNEYS: {
    path: '/journeys/audiencerule/new/audience-editor',
    message: "You don't have any Journeys data yet. Create a Journeys banner to get started.",
  },
};
const ANALYTICS_DEFAULT_NO_DATA_MSG = 'No analytics found. Try adjusting the date range or changing filters.';
const TABLE_DEFAULT_NO_DATA_MSG =
  'No analytics found. Try adjusting the date range, changing the filters, or adding columns.';
const IMG_TABLE = '/static/images/emptyState_Table.svg';
const IMG_TIMESERIES = '/static/images/emptyState_Analytics.svg';
type Props = {
  analyticsType: 'table' | 'timeseries';
  product?: 'ADS' | 'JOURNEYS' | 'EMAILS' | 'ROI';
  message?: ReactElement;
  ad_links: Record<string, any>;
  journey: Record<string, any>;
  getAdLinks: () => void;
  getJourneys: () => void;
};

class AnalyticsNoDataMessage extends PureComponent<Props> {
  componentWillMount() {
    if (this.props.product === 'ADS' && !this.props.ad_links.links) {
      this.props.getAdLinks();
    }

    if (this.props.product === 'JOURNEYS' && !this.props.journey.rules) {
      this.props.getJourneys();
    }
  }

  checkForUsage = () => {
    const { ad_links, journey } = this.props;
    const data = ad_links.links || journey.rules || [];
    return !!data.length;
  };

  isLoading = () => {
    const { product, ad_links, journey } = this.props;

    if (product === 'ADS' && ad_links.loading) {
      return true;
    }

    // TODO: loadingStatus is not updated correctly in JourneyReducer,
    // so this condition will never be true
    const journeyLoading = journey.loadingStatus === LOADING_STATUS.LOADING;

    if (product === 'JOURNEYS' && journeyLoading) {
      return true;
    }

    return false;
  };

  renderMessage = () => {
    const { product, analyticsType } = this.props;
    const usage = this.checkForUsage();

    if (product && PRODUCTS.hasOwnProperty(product) && !usage) {
      return PRODUCTS[product].message;
    }

    return analyticsType === 'table' ? TABLE_DEFAULT_NO_DATA_MSG : ANALYTICS_DEFAULT_NO_DATA_MSG;
  };

  renderCta = (message) => {
    const { product, analyticsType } = this.props;
    const usage = this.checkForUsage();

    if (!product || !PRODUCTS.hasOwnProperty(product) || usage) {
      return null;
    }

    // biome-ignore lint/suspicious/noDoubleEquals: ignored using `--suppress`
    if (message != PRODUCTS[product].message) {
      return null;
    }

    return (
      <Button type="primary" size="medium" testid={`${analyticsType}-no-data-cta`} to={PRODUCTS[product].path}>
        Get Started
      </Button>
    );
  };

  render() {
    const { analyticsType } = this.props;

    if (this.isLoading()) {
      return null;
    }

    const image = analyticsType === 'table' ? IMG_TABLE : IMG_TIMESERIES;
    // message passed in from props takes precedence
    const message = this.props.message || this.renderMessage();
    const cta = this.renderCta(message);
    return (
      <div className="error__message">
        <div className="h_maw-500">
          <img className="h_mb-20" src={ImageService.getImageUrl(image)} />
          <div>{message}</div>
          <div>{cta}</div>
        </div>
      </div>
    );
  }
}

const mapDispatchProps = (dispatch) => ({
  getAdLinks: () => dispatch(getAdLinks()),
  getJourneys: () => dispatch(fetchRules()),
});

function mapStateToProps(state) {
  return {
    ad_links: state.ad_links,
    journey: state.journey.journeysLegacy,
  };
}

export default connect(mapStateToProps, mapDispatchProps)(AnalyticsNoDataMessage);
